import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import { StatusBadge, severityFromScore, RiskBadge } from "@/components/StatusBadge";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";

const SEV_LABEL = { optimal: "Ready", warning: "Needs Work", critical: "Blocked" };
const SEV_LABEL_AR = { optimal: "جاهز", warning: "يحتاج عمل", critical: "محظور" };
const BAR = { optimal: "bg-emerald-500", warning: "bg-amber-500", critical: "bg-rose-500" };

export default function AbsorptionCenter() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/absorption/summary").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  const getContext = () => ({ absorptionReadiness: data.absorptionReadiness, dimensions: data.dimensions,
    atRiskCount: data.atRiskCount, atRiskSample: data.atRisk.slice(0, 4).map((c) => c.name) });

  return (
    <div className="space-y-6 fade-up" data-testid="absorption-page">
      <PageHeader title={t("absorption_center")} subtitle={t("absorption_desc")} screen="absorption" getContext={getContext} />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-2xl bg-primary text-primary-foreground shadow-md flex flex-col items-center justify-center text-center">
          <Target className="h-8 w-8 mb-2" />
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/70">{t("absorption_readiness")}</p>
          <p className="text-6xl font-bold my-1">{data.absorptionReadiness}<span className="text-2xl">%</span></p>
          <p className="text-sm text-primary-foreground/80">{data.atRiskCount} {t("at_risk_absorption")}</p>
        </Card>

        <Card className="lg:col-span-2 p-5 sm:p-6 rounded-2xl shadow-sm">
          <h3 className="text-lg font-semibold mb-4">{t("readiness_dimensions")}</h3>
          <div className="space-y-4">
            {Object.entries(data.dimensions).map(([dim, score]) => {
              const sev = severityFromScore(score);
              return (
                <div key={dim} data-testid={`dimension-${dim}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium">{dim}</span>
                    <div className="flex items-center gap-2">
                      <StatusBadge severity={sev} label={lang === "ar" ? SEV_LABEL_AR[sev] : SEV_LABEL[sev]} />
                      <span className="text-sm font-bold w-10 text-end">{score}%</span>
                    </div>
                  </div>
                  <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full ${BAR[sev]}`} style={{ width: `${score}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl shadow-sm">
        <h3 className="text-lg font-semibold mb-4">{t("at_risk_absorption")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.atRisk.map((c) => (
            <button key={c.uid} onClick={() => navigate(`/use-cases/${c.uid}`)} data-testid={`absorption-risk-${c.uid}`}
              className="flex items-center gap-3 rounded-xl border p-3.5 text-start hover:bg-secondary transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{lang === "ar" ? c.name_ar : c.name}</p>
                <p className="text-xs text-muted-foreground">{t("absorption")}: {c.scores.absorption}%</p>
              </div>
              <RiskBadge risk={c.riskLevel} />
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 rtl:rotate-180" />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
